import { useCallback } from 'react';
import { useAppContext } from '@/context/AppContext';
import { XMLOperationsActions } from '@/types';

export function useXMLOperations(): XMLOperationsActions {
  const { state, dispatch } = useAppContext();

  const addVehiclesToGroup = useCallback((groupElement: Element, modelNames: string[]) => {
    const xmlDoc = state.xmlDoc;
    if (!xmlDoc) return;

    const modelsContainer = groupElement.querySelector('models');
    if (!modelsContainer) return;

    const existingModels = new Set(
      Array.from(modelsContainer.querySelectorAll('Item > Name, Item > n'))
        .map(el => el.textContent || '')
        .filter(Boolean)
    );
    const duplicates: string[] = [];
    let didAdd = false;

    modelNames.forEach(modelName => {
      if (existingModels.has(modelName)) {
        duplicates.push(modelName);
        return;
      }

      existingModels.add(modelName);

      const newModel = xmlDoc.createElement('Item');
      const newModelName = xmlDoc.createElement('Name');
      newModelName.textContent = modelName;
      newModel.appendChild(newModelName);

      const variations = xmlDoc.createElement('Variations');
      newModel.appendChild(variations);

      modelsContainer.appendChild(newModel);
      didAdd = true;
    });

    if (duplicates.length === 1) {
      dispatch({ type: 'ADD_ERROR', payload: `Vehicle "${duplicates[0]}" is already in this group` });
    } else if (duplicates.length > 1) {
      const preview = duplicates.slice(0, 5).join(', ');
      const suffix = duplicates.length > 5 ? ` (+${duplicates.length - 5} more)` : '';
      dispatch({ type: 'ADD_ERROR', payload: `Some vehicles are already in this group: ${preview}${suffix}` });
    }

    if (didAdd) {
      dispatch({ type: 'MARK_UNSAVED' });
      dispatch({ type: 'UPDATE_XML_DOC', payload: xmlDoc });
    }
  }, [state.xmlDoc, dispatch]);

  const addVehicleToGroup = useCallback((groupElement: Element, modelName: string) => {
    addVehiclesToGroup(groupElement, [modelName]);
  }, [addVehiclesToGroup]);

  const removeVehicleFromGroup = useCallback((groupElement: Element, modelName: string) => {
    const xmlDoc = state.xmlDoc;
    if (!xmlDoc) return;
    const modelElements = groupElement.querySelectorAll('models > Item > Name, models > Item > n');

    for (const element of modelElements) {
      if (element.textContent === modelName) {
        const parentElement = element.parentNode;
        if (parentElement && parentElement.parentNode) {
          parentElement.parentNode.removeChild(parentElement);
        }
        dispatch({ type: 'MARK_UNSAVED' });
        dispatch({ type: 'UPDATE_XML_DOC', payload: xmlDoc });
        break;
      }
    }
  }, [dispatch, state.xmlDoc]);

  return {
    addVehicleToGroup,
    addVehiclesToGroup,
    removeVehicleFromGroup
  };
}
